import { basicSetup } from "codemirror";
import { EditorSelection, EditorState, RangeSetBuilder, StateEffect, type Extension } from "@codemirror/state";
import {
  Decoration,
  EditorView,
  ViewPlugin,
  WidgetType,
  type DecorationSet,
  type ViewUpdate
} from "@codemirror/view";
import { markdown } from "@codemirror/lang-markdown";
import { WebsocketProvider } from "y-websocket";
import * as Y from "yjs";
import { yCollab } from "y-codemirror.next";

import {
  archiveCommentThreadInDoc,
  createCommentThreadInDoc,
  deleteCommentThreadInDoc,
  replyToCommentThreadInDoc,
  serializeCommentThreads
} from "../src/domain/commentDocument.js";
import type { CommentTarget, CommentThread } from "../src/domain/types.js";
import {
  getCollaboratorCursorOffset,
  parseCollaboratorIdentity,
  parseCollaboratorSelection,
  type CollaboratorIdentity,
  type CollaboratorPresence,
  type CollaboratorSelection
} from "../src/shortTerm/awareness.js";
import {
  getSharedImageAssets,
  observeSharedImageAssets,
  type SharedImageAsset
} from "../src/shortTerm/sharedAssets.js";

type ThemeMode = "light" | "dark";

interface WebPreferences {
  serverUrl?: string;
  displayName?: string;
  theme?: ThemeMode;
  primaryColor?: string;
  editorFontSize?: number;
  commentsPaneWidth?: number;
}

interface SelectionSnapshot {
  selection: CollaboratorSelection;
  selectedText: string;
}

interface LocalSelectionState extends SelectionSnapshot {
  summary: string;
}

const storageKey = "obisession-web-preferences";
const localClientId = crypto.randomUUID();
const localColor = "#7d828c";
const refreshEditorDecorationsEffect = StateEffect.define<void>();

const elements = {
  body: document.body,
  joinForm: getElement<HTMLFormElement>("join-form"),
  joinButton: getElement<HTMLButtonElement>("join-button"),
  leaveButton: getElement<HTMLButtonElement>("leave-button"),
  joinHint: getElement<HTMLElement>("join-hint"),
  commentsToggle: getElement<HTMLButtonElement>("comments-toggle"),
  fullscreenToggle: getElement<HTMLButtonElement>("fullscreen-toggle"),
  commentsPane: getElement<HTMLElement>("comments-pane"),
  commentsFlyoutBackdrop: getElement<HTMLElement>("comments-flyout-backdrop"),
  fullscreenControls: getElement<HTMLElement>("fullscreen-controls"),
  fullscreenCommentsToggle: getElement<HTMLButtonElement>("fullscreen-comments-toggle"),
  fullscreenExit: getElement<HTMLButtonElement>("fullscreen-exit"),
  settingsToggle: getElement<HTMLButtonElement>("settings-toggle"),
  settingsMenu: getElement<HTMLElement>("settings-menu"),
  themeLight: getElement<HTMLButtonElement>("theme-light"),
  themeDark: getElement<HTMLButtonElement>("theme-dark"),
  editorFontSize: getElement<HTMLInputElement>("editor-font-size"),
  editorFontSizeValue: getElement<HTMLElement>("editor-font-size-value"),
  primaryColor: getElement<HTMLInputElement>("primary-color"),
  primaryColorValue: getElement<HTMLElement>("primary-color-value"),
  serverUrl: getElement<HTMLInputElement>("server-url"),
  roomId: getElement<HTMLInputElement>("room-id"),
  displayName: getElement<HTMLInputElement>("display-name"),
  connectionPill: getElement<HTMLElement>("connection-pill"),
  connectionStatus: getElement<HTMLElement>("connection-status"),
  selectionState: getElement<HTMLElement>("selection-state"),
  syncStatus: getElement<HTMLElement>("sync-status"),
  wordCount: getElement<HTMLElement>("word-count"),
  characterCount: getElement<HTMLElement>("character-count"),
  editorFrame: getElement<HTMLElement>("editor-frame"),
  editorSurface: getElement<HTMLElement>("editor-surface"),
  editorEmptyState: getElement<HTMLElement>("editor-empty-state"),
  editorRoot: getElement<HTMLElement>("editor-root"),
  commentsResizer: getElement<HTMLElement>("comments-resizer"),
  cursorOverlay: getElement<HTMLElement>("cursor-overlay"),
  commentPopover: getElement<HTMLElement>("comment-popover"),
  commentPopoverButton: getElement<HTMLButtonElement>("comment-popover-button"),
  commentModal: getElement<HTMLElement>("comment-modal"),
  commentModalClose: getElement<HTMLButtonElement>("comment-modal-close"),
  commentModalTitle: getElement<HTMLElement>("comment-modal-title"),
  commentModalTarget: getElement<HTMLElement>("comment-modal-target"),
  commentModalThreadContent: getElement<HTMLElement>("comment-modal-thread-content"),
  commentModalForm: getElement<HTMLFormElement>("comment-modal-form"),
  commentModalLabel: getElement<HTMLElement>("comment-modal-label"),
  commentModalInput: getElement<HTMLTextAreaElement>("comment-modal-input"),
  commentModalSubmit: getElement<HTMLButtonElement>("comment-modal-submit"),
  commentModalDelete: getElement<HTMLButtonElement>("comment-modal-delete"),
  commentModalArchive: getElement<HTMLButtonElement>("comment-modal-archive"),
  threadList: getElement<HTMLElement>("thread-list"),
  threadListEmpty: getElement<HTMLElement>("thread-list-empty"),
  presenceList: getElement<HTMLElement>("presence-list")
};

let session: WebSession | null = null;
let pendingTheme: ThemeMode = loadThemePreference();
let pendingPrimaryColor = loadPrimaryColorPreference();
let pendingEditorFontSize = loadEditorFontSizePreference();
let pendingCommentsPaneWidth = loadCommentsPaneWidthPreference();
let currentDisplayName = generateFallbackName(localClientId);
let currentSelection: LocalSelectionState | null = null;
const commentsFlyoutMediaQuery = window.matchMedia("(max-width: 980px), (orientation: portrait)");
const commentState = {
  threads: [] as CommentThread[],
  activeThreadId: null as string | null,
  replyDraft: ""
};
const commentModalState = {
  mode: "closed" as "closed" | "new-thread" | "thread",
  threadId: null as string | null,
  target: null as CommentTarget | null
};
let shouldFocusCommentModalInput = false;

applyTheme(pendingTheme);
applyPrimaryColor(pendingPrimaryColor);
applyEditorFontSize(pendingEditorFontSize);
applyCommentsPaneWidth(pendingCommentsPaneWidth);
hydratePreferences();
renderPresence([]);
renderCommentPanel();
renderSettingsMenu();
updateEditorMetrics();
syncCommentsFlyoutMode();
updateConnectionUi("disconnected");

function updateConnectionUi(status: string): void {
  const normalized = status.trim().toLowerCase();
  const isConnected = normalized === "connected";
  const visualState =
    normalized === "connected" ? "connected" : normalized === "connecting" ? "connecting" : "disconnected";

  elements.connectionStatus.textContent =
    visualState === "connected" ? "Connected" : visualState === "connecting" ? "Connecting" : "Disconnected";
  elements.connectionPill.dataset.connectionState = visualState;
  elements.joinButton.hidden = isConnected;
  elements.leaveButton.hidden = !isConnected;
}

function setSettingsMenuOpen(isOpen: boolean): void {
  elements.settingsMenu.classList.toggle("is-open", isOpen);
  elements.settingsMenu.setAttribute("aria-hidden", String(!isOpen));
  elements.settingsToggle.setAttribute("aria-expanded", String(isOpen));
  document.body.classList.toggle("settings-flyout-open", isCommentsFlyoutMode() && isOpen);
  syncFlyoutBackdrop();
}

function isCommentsFlyoutMode(): boolean {
  return commentsFlyoutMediaQuery.matches;
}

function isFullscreenMode(): boolean {
  return document.body.classList.contains("fullscreen-mode");
}

function setCommentsFlyoutOpen(isOpen: boolean): void {
  const shouldOpen = isCommentsFlyoutMode() ? isOpen : false;
  if (shouldOpen) {
    setSettingsMenuOpen(false);
  }
  document.body.classList.toggle("comments-flyout-open", shouldOpen);
  elements.commentsToggle.setAttribute("aria-expanded", String(shouldOpen));
  elements.fullscreenCommentsToggle.setAttribute("aria-expanded", String(shouldOpen));
  syncFlyoutBackdrop();
}

function syncCommentsFlyoutMode(): void {
  elements.commentsToggle.hidden = !isCommentsFlyoutMode();
  elements.fullscreenCommentsToggle.hidden = !isCommentsFlyoutMode() || !isFullscreenMode();
  if (!isCommentsFlyoutMode()) {
    setCommentsFlyoutOpen(false);
    document.body.classList.remove("settings-flyout-open");
    syncFlyoutBackdrop();
  }
}

function setFullscreenMode(isOpen: boolean): void {
  setSettingsMenuOpen(false);
  document.body.classList.toggle("fullscreen-mode", isOpen);
  elements.fullscreenControls.hidden = !isOpen;
  elements.fullscreenToggle.setAttribute("aria-expanded", String(isOpen));
  elements.fullscreenToggle.setAttribute(
    "aria-label",
    isOpen ? "Exit fullscreen editing" : "Enter fullscreen editing"
  );
  elements.fullscreenToggle.setAttribute("title", isOpen ? "Exit fullscreen" : "Fullscreen");
  syncCommentsFlyoutMode();
  if (!isOpen) {
    setCommentsFlyoutOpen(false);
    setSettingsMenuOpen(false);
  }
}

function syncFlyoutBackdrop(): void {
  const isOpen =
    document.body.classList.contains("comments-flyout-open") ||
    document.body.classList.contains("settings-flyout-open");
  elements.commentsFlyoutBackdrop.hidden = !isOpen;
}

elements.joinForm.addEventListener("submit", async (event) => {
  event.preventDefault();

  const serverUrl = elements.serverUrl.value.trim().replace(/\/+$/, "");
  const roomId = elements.roomId.value.trim().toLowerCase();
  const displayName = elements.displayName.value.trim() || generateFallbackName(localClientId);

  if (!serverUrl) {
    setStatus("Server URL is required.");
    return;
  }

  if (!/^[a-z0-9]{8}$/.test(roomId)) {
    setStatus("Room token must be 8 lowercase letters or digits.");
    return;
  }

  await session?.destroy();
  session = new WebSession({
    serverUrl,
    roomId,
    identity: {
      clientId: localClientId,
      displayName,
      color: localColor
    },
    onSelectionChange: (selection) => {
      currentSelection = selection;
      renderCommentPanel();
    },
    onCommentsChange: (threads) => {
      commentState.threads = threads;
      if (commentState.activeThreadId && !threads.some((thread) => thread.id === commentState.activeThreadId)) {
        commentState.activeThreadId = null;
      }
      renderCommentPanel();
      renderCommentModal();
    },
    onPresenceChange: renderPresence,
    onConnectionChange: (status) => {
      updateConnectionUi(status);
    },
    onSyncMessage: (message) => {
      elements.syncStatus.textContent = message;
    }
  });

  await session.connect(elements.editorRoot);
  currentDisplayName = displayName;
  savePreferences({
    serverUrl,
    displayName,
    theme: pendingTheme,
    primaryColor: pendingPrimaryColor,
    editorFontSize: pendingEditorFontSize,
    commentsPaneWidth: pendingCommentsPaneWidth
  });
});

elements.leaveButton.addEventListener("click", async () => {
  await session?.destroy();
  session = null;
  updateConnectionUi("disconnected");
  elements.syncStatus.textContent = "Idle";
  currentSelection = null;
  commentState.threads = [];
  commentState.activeThreadId = null;
  commentState.replyDraft = "";
  closeCommentModal();
  renderCommentPanel();
  renderPresence([]);
  setCommentsFlyoutOpen(false);
});

elements.commentsToggle.addEventListener("click", () => {
  setCommentsFlyoutOpen(!document.body.classList.contains("comments-flyout-open"));
});

elements.fullscreenCommentsToggle.addEventListener("click", () => {
  setCommentsFlyoutOpen(!document.body.classList.contains("comments-flyout-open"));
});

elements.commentsFlyoutBackdrop.addEventListener("click", () => {
  setCommentsFlyoutOpen(false);
  setSettingsMenuOpen(false);
});

elements.fullscreenToggle.addEventListener("click", () => {
  setFullscreenMode(!isFullscreenMode());
});

elements.fullscreenExit.addEventListener("click", () => {
  setFullscreenMode(false);
});

elements.settingsToggle.addEventListener("click", (event) => {
  event.stopPropagation();
  if (isCommentsFlyoutMode() && !elements.settingsMenu.classList.contains("is-open")) {
    setCommentsFlyoutOpen(false);
  }
  setSettingsMenuOpen(!elements.settingsMenu.classList.contains("is-open"));
});

elements.themeLight.addEventListener("click", () => {
  setThemePreference("light");
});

elements.themeDark.addEventListener("click", () => {
  setThemePreference("dark");
});

elements.primaryColor.addEventListener("input", () => {
  pendingPrimaryColor = normalizePrimaryColor(elements.primaryColor.value);
  applyPrimaryColor(pendingPrimaryColor);
  renderSettingsMenu();
  const preferences = loadPreferences();
  savePreferences({
    ...preferences,
    primaryColor: pendingPrimaryColor
  });
});

elements.serverUrl.addEventListener("input", () => {
  savePreferences({
    ...loadPreferences(),
    serverUrl: elements.serverUrl.value.trim()
  });
});

elements.editorFontSize.addEventListener("input", () => {
  pendingEditorFontSize = normalizeEditorFontSize(Number(elements.editorFontSize.value));
  applyEditorFontSize(pendingEditorFontSize);
  renderSettingsMenu();
  savePreferences({
    ...loadPreferences(),
    editorFontSize: pendingEditorFontSize
  });
});

elements.commentPopoverButton.addEventListener("click", () => {
  void createCommentThreadFromSelection();
});

elements.commentPopoverButton.addEventListener("pointerdown", (event) => {
  event.preventDefault();
});

elements.commentPopoverButton.addEventListener("mousedown", (event) => {
  event.preventDefault();
});

elements.commentModalClose.addEventListener("click", () => {
  closeCommentModal();
});

elements.commentModalForm.addEventListener("submit", (event) => {
  event.preventDefault();
  void submitCommentModal();
});

elements.commentModalSubmit.addEventListener("click", (event) => {
  event.preventDefault();
  void submitCommentModal();
});

elements.commentModalInput.addEventListener("input", () => {
  commentState.replyDraft = elements.commentModalInput.value;
  updateCommentModalSubmitState();
});

elements.commentModalInput.addEventListener("keydown", (event) => {
  if (event.key !== "Enter") {
    return;
  }

  if (event.altKey) {
    return;
  }

  event.preventDefault();
  void submitCommentModal();
});

elements.commentModalDelete.addEventListener("click", () => {
  void deleteCommentModalThread();
});

elements.commentModalArchive.addEventListener("click", () => {
  void archiveCommentModalThread();
});

elements.commentModal.addEventListener("click", (event) => {
  const target = event.target as HTMLElement;
  if (target.hasAttribute("data-close-comment-modal")) {
    closeCommentModal();
  }
});

window.addEventListener("keydown", (event) => {
  if (event.key === "Escape" && document.body.classList.contains("comments-flyout-open")) {
    setCommentsFlyoutOpen(false);
    return;
  }

  if (event.key === "Escape" && document.body.classList.contains("settings-flyout-open")) {
    setSettingsMenuOpen(false);
    return;
  }

  if (event.key === "Escape" && commentModalState.mode !== "closed") {
    closeCommentModal();
    return;
  }

  if (event.key === "Escape" && elements.settingsMenu.classList.contains("is-open")) {
    setSettingsMenuOpen(false);
    return;
  }

  if (event.key === "Escape" && isFullscreenMode()) {
    setFullscreenMode(false);
  }
});

commentsFlyoutMediaQuery.addEventListener("change", () => {
  syncCommentsFlyoutMode();
});

window.addEventListener("click", (event) => {
  const target = event.target as Node | null;
  if (!target) {
    return;
  }

  if (
    elements.settingsMenu.classList.contains("is-open") &&
    !elements.settingsMenu.contains(target) &&
    !elements.settingsToggle.contains(target)
  ) {
    setSettingsMenuOpen(false);
  }
});

bindCommentsPaneResizer();

interface ImageEmbedMatch {
  from: number;
  to: number;
  referencePath: string;
  width: number | null;
}

function createWebEditorExtensions(session: WebSession): Extension[] {
  return [
    basicSetup,
    markdown(),
    EditorView.lineWrapping,
    EditorView.updateListener.of((update) => {
      session.handleEditorUpdate(update);
    }),
    createCommentAndAssetExtension(session),
    EditorView.theme({
      "&": {
        height: "100%",
      },
      ".cm-scroller": {
        overflow: "auto",
        minHeight: "100%",
        fontFamily: "var(--font-mono)",
      },
      ".cm-content": {
        minHeight: "100%",
        padding: "24px 24px 34px",
        fontSize: "var(--editor-font-size)",
        lineHeight: "1.8",
        caretColor: "var(--primary-strong)",
      },
      ".cm-line": {
        padding: "0",
      },
      ".cm-gutters": {
        display: "none",
      },
      ".cm-activeLine": {
        backgroundColor: "transparent",
      },
      ".cm-activeLineGutter": {
        backgroundColor: "transparent",
      },
      ".cm-selectionBackground, ::selection": {
        backgroundColor: "color-mix(in srgb, var(--primary) 28%, transparent) !important",
      },
    })
  ];
}

function createCommentAndAssetExtension(session: WebSession): Extension {
  return ViewPlugin.fromClass(
    class {
      public decorations: DecorationSet;

      public constructor(view: EditorView) {
        this.decorations = buildEditorDecorations(view, session);
      }

      public update(update: ViewUpdate): void {
        if (
          update.docChanged ||
          update.selectionSet ||
          update.viewportChanged ||
          update.transactions.some((transaction) =>
            transaction.effects.some((effect) => effect.is(refreshEditorDecorationsEffect))
          )
        ) {
          this.decorations = buildEditorDecorations(update.view, session);
        }
      }
    },
    {
      decorations: (value) => value.decorations
    }
  );
}

function buildEditorDecorations(view: EditorView, session: WebSession): DecorationSet {
  const builder = new RangeSetBuilder<Decoration>();
  const documentText = view.state.doc.toString();
  const selection = view.state.selection.main;
  const activeThreads = commentState.threads.filter((thread) => thread.status === "active");
  const decorations: Array<{
    from: number;
    to: number;
    startSide: number;
    decoration: Decoration;
  }> = [];

  for (const match of findImageEmbeds(documentText)) {
    const intersectsSelection = selection.from <= match.to && selection.to >= match.from;
    if (intersectsSelection) {
      continue;
    }

    decorations.push({
      from: match.from,
      to: match.to,
      startSide: 0,
      decoration: Decoration.replace({
        block: true,
        widget: new ImageEmbedWidget(match, session.getSharedImageAsset(match.referencePath), session)
      })
    });
  }

  for (const thread of activeThreads) {
    if (thread.target.kind === "text-range" || thread.target.kind === "table-cell") {
      const from = Math.max(0, Math.min(thread.target.start, view.state.doc.length));
      const to = Math.max(from, Math.min(thread.target.end, view.state.doc.length));
      if (from === to) {
        continue;
      }

      decorations.push({
        from,
        to,
        startSide: 0,
        decoration: Decoration.mark({
          class: "cm-comment-highlight",
          attributes: {
            "data-thread-id": thread.id,
            role: "button",
            tabindex: "0",
            "aria-label": `Open comment from ${thread.owner.displayName}`
          }
        })
      });
      decorations.push({
        from: to,
        to,
        startSide: 1,
        decoration: Decoration.widget({
          side: 1,
          widget: new ThreadBubbleWidget(thread.id, thread.owner.displayName)
        })
      });
    }
  }

  decorations.sort((left, right) => {
    if (left.from !== right.from) {
      return left.from - right.from;
    }
    if (left.startSide !== right.startSide) {
      return left.startSide - right.startSide;
    }
    if (left.to !== right.to) {
      return left.to - right.to;
    }
    return 0;
  });

  for (const item of decorations) {
    builder.add(item.from, item.to, item.decoration);
  }

  return builder.finish();
}

function findImageEmbeds(markdown: string): ImageEmbedMatch[] {
  const matches: ImageEmbedMatch[] = [];
  const expression = /!\[\[([^[\]|]+)(?:\|([^\]]+))?\]\]/g;
  for (const match of markdown.matchAll(expression)) {
    const fullMatch = match[0];
    const rawPath = match[1]?.trim();
    if (!fullMatch || !rawPath || match.index === undefined) {
      continue;
    }

    const rawWidth = match[2]?.trim() ?? "";
    const parsedWidth = Number(rawWidth);
    matches.push({
      from: match.index,
      to: match.index + fullMatch.length,
      referencePath: rawPath,
      width: Number.isFinite(parsedWidth) && parsedWidth > 0 ? parsedWidth : null
    });
  }

  return matches;
}

class ThreadBubbleWidget extends WidgetType {
  public constructor(
    private readonly threadId: string,
    private readonly displayName: string
  ) {
    super();
  }

  public override toDOM(): HTMLElement {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "cm-comment-bubble";
    button.textContent = "💬";
    button.title = `Open comment from ${this.displayName}`;
    button.dataset.threadId = this.threadId;
    return button;
  }
}

class ImageEmbedWidget extends WidgetType {
  public constructor(
    private readonly match: ImageEmbedMatch,
    private readonly asset: SharedImageAsset | null,
    private readonly session: WebSession
  ) {
    super();
  }

  public override eq(other: ImageEmbedWidget): boolean {
    return (
      this.match.referencePath === other.match.referencePath &&
      this.match.width === other.match.width &&
      this.asset?.sourcePath === other.asset?.sourcePath &&
      this.session.getImageThreadSignature(this.match.referencePath) ===
        other.session.getImageThreadSignature(other.match.referencePath)
    );
  }

  public override toDOM(): HTMLElement {
    const wrapper = document.createElement("div");
    wrapper.className = "cm-image-embed";
    wrapper.dataset.referencePath = this.match.referencePath;

    const frame = document.createElement("div");
    frame.className = "cm-image-embed-frame";

    if (this.asset) {
      const image = document.createElement("img");
      image.className = "cm-image-embed-image";
      image.alt = this.match.referencePath;
      image.src = this.session.getOrCreateAssetUrl(this.asset);
      image.loading = "lazy";
      if (this.match.width !== null) {
        image.style.maxWidth = `${this.match.width}px`;
      }
      frame.appendChild(image);
    } else {
      const missing = document.createElement("div");
      missing.className = "cm-image-embed-missing";
      missing.textContent = this.match.referencePath;
      frame.appendChild(missing);
    }

    for (const thread of commentState.threads) {
      if (thread.status !== "active" || thread.target.kind !== "image-position") {
        continue;
      }
      if (!this.session.matchesImageReference(thread.target.imagePath, this.match.referencePath)) {
        continue;
      }

      const pin = document.createElement("button");
      pin.type = "button";
      pin.className = "cm-image-comment-pin";
      pin.textContent = "💬";
      pin.title = `Open comment from ${thread.owner.displayName}`;
      pin.dataset.threadId = thread.id;
      pin.style.left = `${thread.target.x * 100}%`;
      pin.style.top = `${thread.target.y * 100}%`;
      frame.appendChild(pin);
    }

    const caption = document.createElement("div");
    caption.className = "cm-image-embed-caption";
    caption.textContent = this.match.referencePath.split("/").pop() ?? this.match.referencePath;

    wrapper.append(frame, caption);
    return wrapper;
  }
}

class WebSession {
  private readonly doc = new Y.Doc();
  private readonly text = this.doc.getText("markdown");
  private readonly provider: WebsocketProvider;
  private readonly identity: CollaboratorIdentity;
  private readonly roomId: string;
  private readonly onSelectionChange: (selection: LocalSelectionState | null) => void;
  private readonly onCommentsChange: (threads: CommentThread[]) => void;
  private readonly onPresenceChange: (collaborators: CollaboratorPresence[]) => void;
  private readonly onConnectionChange: (status: string, roomId?: string) => void;
  private readonly onSyncMessage: (message: string) => void;
  private unobserveText?: () => void;
  private awarenessInterval?: number;
  private presenceListener?: () => void;
  private lastCommentSnapshot = "";
  private readonly rerenderRemoteCursors = (): void => {
    this.renderRemoteCursors();
    renderSelectionPopover();
  };
  private currentCollaborators: CollaboratorPresence[] = [];
  private unobserveAssets?: () => void;
  private assetObjectUrls = new Map<string, string>();
  private currentAssets: SharedImageAsset[] = [];
  private view?: EditorView;

  public constructor(options: {
    serverUrl: string;
    roomId: string;
    identity: CollaboratorIdentity;
    onSelectionChange: (selection: LocalSelectionState | null) => void;
    onCommentsChange: (threads: CommentThread[]) => void;
    onPresenceChange: (collaborators: CollaboratorPresence[]) => void;
    onConnectionChange: (status: string, roomId?: string) => void;
    onSyncMessage: (message: string) => void;
  }) {
    this.roomId = options.roomId;
    this.identity = options.identity;
    this.onSelectionChange = options.onSelectionChange;
    this.onCommentsChange = options.onCommentsChange;
    this.onPresenceChange = options.onPresenceChange;
    this.onConnectionChange = options.onConnectionChange;
    this.onSyncMessage = options.onSyncMessage;
    this.provider = new WebsocketProvider(options.serverUrl, options.roomId, this.doc);
  }

  public async connect(parent: HTMLElement): Promise<void> {
    this.view = new EditorView({
      state: EditorState.create({
        doc: this.text.toString(),
        extensions: [
          ...createWebEditorExtensions(this),
          yCollab(this.text, this.provider.awareness)
        ]
      }),
      parent
    });

    this.view.dom.addEventListener("click", (event) => {
      const target = event.target as HTMLElement | null;
      const threadTarget = target?.closest<HTMLElement>("[data-thread-id]");
      if (threadTarget?.dataset.threadId) {
        event.preventDefault();
        event.stopPropagation();
        void openCommentModalForThread(threadTarget.dataset.threadId);
      }
    });

    this.view.dom.addEventListener("keydown", (event) => {
      if ((event.key === "Enter" || event.key === " ") && event.target instanceof HTMLElement) {
        const threadTarget = event.target.closest<HTMLElement>("[data-thread-id]");
        if (threadTarget?.dataset.threadId) {
          event.preventDefault();
          event.stopPropagation();
          void openCommentModalForThread(threadTarget.dataset.threadId);
        }
      }
    });

    updateEditorMetrics();
    this.onConnectionChange("Connecting", this.roomId);
    this.onSyncMessage("Syncing room state…");

    this.provider.awareness.setLocalStateField("user", this.identity);
    this.publishSelection();

    this.provider.on("status", (event) => {
      this.onConnectionChange(capitalize(event.status), this.roomId);
    });
    this.provider.on("sync", (isSynced: boolean) => {
      this.onSyncMessage(isSynced ? "Live and synced." : "Waiting for sync…");
    });

    this.bindTextObserver();
    this.bindAwareness();
    this.bindAssets();
    this.bindOverlayRefresh();

    this.publishPresence();
    this.publishCommentThreads();
    renderSelectionPopover();
    updateConnectionUi("connected");
  }

  public async destroy(): Promise<void> {
    if (this.awarenessInterval !== undefined) {
      window.clearInterval(this.awarenessInterval);
    }

    this.view?.scrollDOM.removeEventListener("scroll", this.rerenderRemoteCursors);
    window.removeEventListener("resize", this.rerenderRemoteCursors);
    this.unobserveText?.();
    this.unobserveAssets?.();
    this.presenceListener?.();
    this.provider.awareness.setLocalState(null);
    this.provider.destroy();
    this.doc.destroy();
    this.currentCollaborators = [];
    this.revokeAssetObjectUrls();
    this.view?.destroy();
    this.view = undefined;
    this.onSelectionChange(null);
    this.onCommentsChange([]);
    this.renderRemoteCursors();
    renderSelectionPopover();
    this.onPresenceChange([]);
  }

  private bindTextObserver(): void {
    const observer = (): void => {
      updateEditorMetrics();
      this.refreshDecorations();
      this.renderRemoteCursors();
      renderSelectionPopover();
    };

    this.text.observe(observer);
    const unobserveText = () => {
      this.text.unobserve(observer);
    };

    const commentObserver = (): void => {
      this.publishCommentThreads();
    };

    this.doc.on("update", commentObserver);
    this.unobserveText = () => {
      unobserveText();
      this.doc.off("update", commentObserver);
    };
  }

  private bindAwareness(): void {
    const awareness = this.provider.awareness;
    const listener = (): void => {
      this.publishPresence();
    };

    awareness.on("change", listener);
    this.presenceListener = () => {
      awareness.off("change", listener);
    };

    this.awarenessInterval = window.setInterval(() => {
      this.publishSelection();
    }, 250);
  }

  private bindAssets(): void {
    this.unobserveAssets = observeSharedImageAssets(this.doc, (assets) => {
      this.currentAssets = assets;
      this.refreshDecorations();
    });
  }

  private bindOverlayRefresh(): void {
    window.addEventListener("resize", this.rerenderRemoteCursors);
    this.view?.scrollDOM.addEventListener("scroll", this.rerenderRemoteCursors);
  }

  private publishSelection(): void {
    const view = this.requireView();
    const selectionRange = view.state.selection.main;
    const selection: CollaboratorSelection = {
      roomId: this.roomId,
      filePath: "web",
      anchor: selectionRange.from,
      head: selectionRange.to
    };

    this.provider.awareness.setLocalStateField("selection", selection);
    const value = view.state.doc.toString();
    this.onSelectionChange({
      selection,
      selectedText: value.slice(
        Math.min(selection.anchor, selection.head),
        Math.max(selection.anchor, selection.head)
      ),
      summary: summarizeSelection(selection, value)
    });
  }

  private publishPresence(): void {
    const collaborators: CollaboratorPresence[] = [];

    for (const [, state] of this.provider.awareness.getStates()) {
      const identity = parseCollaboratorIdentity(state?.user);
      if (!identity || identity.clientId === this.identity.clientId) {
        continue;
      }

      collaborators.push({
        clientId: identity.clientId,
        displayName: identity.displayName,
        color: identity.color,
        selection: parseCollaboratorSelection(state?.selection, this.roomId),
        isLocal: false
      });
    }

    collaborators.sort((left, right) => left.displayName.localeCompare(right.displayName));
    this.currentCollaborators = collaborators;
    this.renderRemoteCursors();
    this.onPresenceChange(collaborators);
  }

  private publishCommentThreads(): void {
    const threads = serializeCommentThreads(this.doc);
    const snapshot = JSON.stringify(threads);
    if (snapshot === this.lastCommentSnapshot) {
      return;
    }

    this.lastCommentSnapshot = snapshot;
    this.onCommentsChange(threads);
  }

  private revokeAssetObjectUrls(): void {
    for (const url of this.assetObjectUrls.values()) {
      URL.revokeObjectURL(url);
    }

    this.assetObjectUrls.clear();
  }

  public async createCommentThread(target: CommentTarget, body: string): Promise<void> {
    createCommentThreadInDoc(
      this.doc,
      {
        target,
        body,
        author: {
          clientId: this.identity.clientId,
          displayName: this.identity.displayName
        }
      },
      this.identity.clientId
    );
  }

  public async replyToThread(threadId: string, body: string): Promise<void> {
    replyToCommentThreadInDoc(
      this.doc,
      {
        threadId,
        body,
        author: {
          clientId: this.identity.clientId,
          displayName: this.identity.displayName
        }
      },
      this.identity.clientId
    );
  }

  public async archiveThread(threadId: string): Promise<void> {
    archiveCommentThreadInDoc(this.doc, threadId, this.identity.clientId, this.identity.clientId);
  }

  public async deleteThread(threadId: string): Promise<void> {
    deleteCommentThreadInDoc(this.doc, threadId, this.identity.clientId, this.identity.clientId);
  }

  private renderRemoteCursors(): void {
    const view = this.view;
    if (!view) {
      elements.cursorOverlay.replaceChildren();
      return;
    }

    const overlay = elements.cursorOverlay;
    overlay.replaceChildren();
    const scrollContainer = view.scrollDOM;
    const surfaceRect = elements.editorSurface.getBoundingClientRect();

    for (const collaborator of this.currentCollaborators) {
      const offset = getCollaboratorCursorOffset(collaborator.selection, view.state.doc.length);
      if (offset === null) {
        continue;
      }

      const coords = view.coordsAtPos(offset);
      if (!coords) {
        continue;
      }

      const bubble = document.createElement("div");
      bubble.className = "cursor-bubble";
      bubble.style.left = `${coords.left - surfaceRect.left + scrollContainer.scrollLeft}px`;
      bubble.style.top = `${coords.top - surfaceRect.top + scrollContainer.scrollTop}px`;

      const line = document.createElement("span");
      line.className = "cursor-bubble-line";

      const label = document.createElement("span");
      label.className = "cursor-bubble-label";
      label.textContent = collaborator.displayName;

      bubble.append(line, label);
      overlay.appendChild(bubble);
    }
  }

  public handleEditorUpdate(update: ViewUpdate): void {
    if (update.docChanged || update.selectionSet || update.focusChanged) {
      this.publishSelection();
      updateEditorMetrics();
      renderSelectionPopover();
    }

    if (update.docChanged || update.selectionSet) {
      this.refreshDecorations();
      this.renderRemoteCursors();
    }

    if (update.focusChanged && !update.view.hasFocus) {
      this.provider.awareness.setLocalStateField("selection", null);
      this.onSelectionChange(null);
      renderSelectionPopover();
    }
  }

  public getDocumentText(): string {
    return this.view?.state.doc.toString() ?? this.text.toString();
  }

  public getMainSelection(): EditorSelection["main"] | null {
    return this.view?.state.selection.main ?? null;
  }

  public getEditorView(): EditorView | null {
    return this.view ?? null;
  }

  public refreshDecorations(): void {
    this.view?.dispatch({
      effects: refreshEditorDecorationsEffect.of(undefined)
    });
  }

  public getSharedImageAsset(referencePath: string): SharedImageAsset | null {
    return findSharedAssetForReference(this.currentAssets, referencePath);
  }

  public matchesImageReference(left: string, right: string): boolean {
    const normalize = (value: string) => value.trim().toLowerCase().split("/").pop() ?? value.trim().toLowerCase();
    return normalize(left) === normalize(right);
  }

  public getImageThreadSignature(referencePath: string): string {
    return commentState.threads
      .filter(
        (thread) =>
          thread.status === "active" &&
          thread.target.kind === "image-position" &&
          this.matchesImageReference(thread.target.imagePath, referencePath)
      )
      .map((thread) => thread.id)
      .join(":");
  }

  public getOrCreateAssetUrl(asset: SharedImageAsset): string {
    let objectUrl = this.assetObjectUrls.get(asset.sourcePath);
    if (!objectUrl) {
      objectUrl = URL.createObjectURL(new Blob([copyAssetBytes(asset.bytes)], { type: asset.mimeType }));
      this.assetObjectUrls.set(asset.sourcePath, objectUrl);
    }
    return objectUrl;
  }

  private requireView(): EditorView {
    if (!this.view) {
      throw new Error("Web session has not been connected to an editor view.");
    }

    return this.view;
  }
}

function hydratePreferences(): void {
  const preferences = loadPreferences();

  elements.serverUrl.value = preferences.serverUrl ?? "ws://localhost:8080/short-term";
  elements.displayName.value = preferences.displayName ?? "";
  elements.editorFontSize.value = String(pendingEditorFontSize);
  elements.editorFontSizeValue.textContent = `${pendingEditorFontSize}px`;
  elements.primaryColor.value = pendingPrimaryColor;
  elements.primaryColorValue.textContent = pendingPrimaryColor.toUpperCase();
}

function bindCommentsPaneResizer(): void {
  let pointerId: number | null = null;
  let startX = 0;
  let startWidth = pendingCommentsPaneWidth;
  let nextWidth = pendingCommentsPaneWidth;
  let frameId = 0;

  const commitResizeFrame = (): void => {
    frameId = 0;
    pendingCommentsPaneWidth = nextWidth;
    applyCommentsPaneWidth(pendingCommentsPaneWidth);
  };

  elements.commentsResizer.addEventListener("pointerdown", (event) => {
    if (window.innerWidth <= 980) {
      return;
    }

    pointerId = event.pointerId;
    startX = event.clientX;
    startWidth = pendingCommentsPaneWidth;
    elements.commentsResizer.setPointerCapture(event.pointerId);
    document.body.classList.add("is-resizing-comments");
    event.preventDefault();
  });

  elements.commentsResizer.addEventListener("pointermove", (event) => {
    if (pointerId !== event.pointerId) {
      return;
    }

    const delta = startX - event.clientX;
    nextWidth = normalizeCommentsPaneWidth(startWidth + delta);
    if (frameId === 0) {
      frameId = window.requestAnimationFrame(commitResizeFrame);
    }
  });

  const finishResize = (): void => {
    if (pointerId === null) {
      return;
    }

    pointerId = null;
    document.body.classList.remove("is-resizing-comments");
    if (frameId !== 0) {
      window.cancelAnimationFrame(frameId);
      commitResizeFrame();
    }
    savePreferences({
      ...loadPreferences(),
      commentsPaneWidth: pendingCommentsPaneWidth
    });
  };

  elements.commentsResizer.addEventListener("pointerup", finishResize);
  elements.commentsResizer.addEventListener("pointercancel", finishResize);
}

function renderPresence(collaborators: CollaboratorPresence[]): void {
  elements.presenceList.replaceChildren();

  if (collaborators.length === 0) {
    return;
  }

  for (const collaborator of collaborators) {
    const avatar = document.createElement("button");
    avatar.type = "button";
    avatar.className = "collaborator-avatar";
    avatar.title = collaborator.displayName;
    avatar.setAttribute("aria-label", collaborator.displayName);
    avatar.dataset.label = collaborator.displayName;
    avatar.textContent = getInitials(collaborator.displayName);
    elements.presenceList.appendChild(avatar);
  }
}

function findSharedAssetForReference(
  assets: SharedImageAsset[],
  referencePath: string
): SharedImageAsset | null {
  const normalizedReference = referencePath.toLowerCase();
  return (
    assets.find((asset) => asset.referencePath.toLowerCase() === normalizedReference) ??
    assets.find((asset) => asset.sourcePath.toLowerCase() === normalizedReference) ??
    assets.find((asset) => asset.sourcePath.split("/").pop()?.toLowerCase() === normalizedReference) ??
    null
  );
}

function copyAssetBytes(bytes: Uint8Array): ArrayBuffer {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy.buffer;
}

function renderCommentPanel(): void {
  const activeThread = commentState.activeThreadId
    ? commentState.threads.find((thread) => thread.id === commentState.activeThreadId) ?? null
    : null;
  const sortedThreads = getSortedThreads();

  elements.selectionState.textContent = currentSelection
    ? currentSelection.summary
    : "No selection";

  renderSelectionPopover();
  updateEditorMetrics();

  elements.threadList.replaceChildren();
  elements.threadListEmpty.hidden = sortedThreads.length > 0;
  elements.threadListEmpty.querySelector("p")!.textContent = "No threads yet.";

  for (const thread of sortedThreads) {
    elements.threadList.appendChild(renderThreadCard(thread, thread.id === activeThread?.id));
  }
}

function getSortedThreads(): CommentThread[] {
  return commentState.threads
    .slice()
    .sort((left, right) => Date.parse(right.updatedAt) - Date.parse(left.updatedAt));
}

function renderThreadCard(thread: CommentThread, isActive: boolean): HTMLElement {
  const card = document.createElement("article");
  card.className = `thread-card${isActive ? " thread-card-active" : ""}`;
  card.dataset.threadId = thread.id;
  card.tabIndex = 0;
  card.setAttribute("aria-pressed", String(isActive));
  card.addEventListener("click", () => {
    void openCommentModalForThread(thread.id);
  });
  card.addEventListener("keydown", (event) => {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }

    event.preventDefault();
    void openCommentModalForThread(thread.id);
  });

  const header = document.createElement("div");
  header.className = "thread-card-header";

  const author = document.createElement("strong");
  const authorSwatch = document.createElement("span");
  authorSwatch.className = "swatch";
  authorSwatch.textContent = getInitials(thread.owner.displayName);
  const authorName = document.createElement("span");
  authorName.textContent = thread.owner.displayName;
  author.append(authorSwatch, authorName);

  const badge = document.createElement("span");
  badge.className = `thread-status thread-status-${thread.status}`;
  badge.textContent = thread.status;

  const target = document.createElement("p");
  target.className = "thread-card-target";
  target.textContent = describeThreadTarget(thread.target);

  const preview = document.createElement("p");
  preview.className = "thread-card-preview";
  preview.textContent = thread.messages[0]?.body ?? "Write the first reply to start this thread.";

  header.append(author, badge);
  card.append(header, target, preview);

  return card;
}

function renderThreadMessage(
  message: CommentThread["messages"][number],
  tone: "root" | "reply"
): HTMLElement {
  const card = document.createElement("article");
  card.className = `thread-message thread-message-${tone}`;

  const header = document.createElement("div");
  header.className = "thread-message-header";

  const author = document.createElement("strong");
  const authorSwatch = document.createElement("span");
  authorSwatch.className = "swatch";
  authorSwatch.textContent = getInitials(message.author.displayName);
  const authorName = document.createElement("span");
  authorName.textContent = message.author.displayName;
  author.append(authorSwatch, authorName);

  const time = document.createElement("span");
  time.textContent = formatRelativeTime(Date.parse(message.createdAt));

  const body = document.createElement("p");
  body.className = "thread-message-body";
  body.textContent = message.body;

  header.append(author, time);
  card.append(header, body);

  return card;
}

function renderCommentModal(): void {
  const isOpen = commentModalState.mode !== "closed";
  elements.commentModal.classList.toggle("is-open", isOpen);
  elements.commentModal.setAttribute("aria-hidden", String(!isOpen));
  document.body.classList.toggle("comment-modal-open", isOpen);

  if (!isOpen) {
    elements.commentModalInput.value = "";
    updateCommentModalSubmitState();
    shouldFocusCommentModalInput = false;
    return;
  }

  const thread =
    commentModalState.mode === "thread" && commentModalState.threadId
      ? commentState.threads.find((item) => item.id === commentModalState.threadId) ?? null
      : null;

  if (commentModalState.mode === "thread" && commentModalState.threadId && !thread) {
    closeCommentModal();
    return;
  }

  const mode = commentModalState.mode;
  const draft = commentState.replyDraft;
  const isThreadMode = mode === "thread" && Boolean(thread);
  const canModerateThread = Boolean(isThreadMode && thread && thread.owner.clientId === localClientId);
  const threadDescription = isThreadMode && thread ? describeThreadTarget(thread.target) : null;

  elements.commentModalTitle.textContent = isThreadMode ? "Thread" : "Comment";
  elements.commentModalTarget.textContent = threadDescription
    ?? (commentModalState.target ? describeThreadTarget(commentModalState.target) : "No target selected");
  elements.commentModalThreadContent.replaceChildren();

  if (isThreadMode && thread) {
    for (const [index, message] of thread.messages.entries()) {
      elements.commentModalThreadContent.appendChild(
        renderThreadMessage(message, index === 0 ? "root" : "reply")
      );
    }
    if (thread.messages.length === 0) {
      const empty = document.createElement("div");
      empty.className = "comment-modal-empty";
      empty.textContent = "No messages yet.";
      elements.commentModalThreadContent.appendChild(empty);
    }
  } else {
    const preview = document.createElement("article");
    preview.className = "comment-modal-preview";
    const target = document.createElement("p");
    target.className = "comment-modal-preview-target";
    target.textContent = commentModalState.target
      ? describeThreadTarget(commentModalState.target)
      : "No target selected";
    const hint = document.createElement("p");
    hint.className = "comment-modal-preview-hint";
    hint.textContent = "Add the first comment to start the thread.";
    preview.append(target, hint);
    elements.commentModalThreadContent.appendChild(preview);
  }

  elements.commentModalLabel.textContent = isThreadMode ? "Reply" : "Write a comment";
  elements.commentModalInput.placeholder = isThreadMode
    ? "Write a reply to this thread..."
    : "Start a thread with a comment...";
  elements.commentModalInput.value = draft;
  elements.commentModalSubmit.textContent = isThreadMode ? "Reply" : "Start thread";
  elements.commentModalDelete.hidden = !isThreadMode;
  elements.commentModalArchive.hidden = !isThreadMode;
  elements.commentModalDelete.disabled = !canModerateThread;
  elements.commentModalArchive.disabled = !canModerateThread;
  updateCommentModalSubmitState();

  if (shouldFocusCommentModalInput) {
    shouldFocusCommentModalInput = false;
    window.requestAnimationFrame(() => {
      elements.commentModalInput.focus();
      const length = elements.commentModalInput.value.length;
      elements.commentModalInput.setSelectionRange(length, length);
    });
  }
}

async function createCommentThreadFromSelection(): Promise<void> {
  if (!session || !isCommentableSelection(currentSelection)) {
    return;
  }

  openNewCommentModal(currentSelection);
}

function openNewCommentModal(selection: LocalSelectionState): void {
  commentModalState.mode = "new-thread";
  commentModalState.threadId = null;
  commentModalState.target = createThreadTarget(selection);
  commentState.replyDraft = "";
  shouldFocusCommentModalInput = true;
  renderCommentModal();
}

function openCommentModalForThread(threadId: string): void {
  const thread = commentState.threads.find((item) => item.id === threadId);
  if (!thread) {
    return;
  }

  commentState.activeThreadId = threadId;
  commentModalState.mode = "thread";
  commentModalState.threadId = threadId;
  commentModalState.target = thread.target;
  shouldFocusCommentModalInput = true;
  renderCommentPanel();
  renderCommentModal();
}

function closeCommentModal(): void {
  commentModalState.mode = "closed";
  commentModalState.threadId = null;
  commentModalState.target = null;
  renderCommentModal();
}

async function submitCommentModal(): Promise<void> {
  if (!session) {
    return;
  }

  const draft = elements.commentModalInput.value.trim();
  if (draft.length === 0) {
    return;
  }

  if (commentModalState.mode === "new-thread" && commentModalState.target) {
    await session.createCommentThread(commentModalState.target, draft);
    commentState.replyDraft = "";
    closeCommentModal();
    renderCommentPanel();
    return;
  }

  if (commentModalState.mode === "thread" && commentModalState.threadId) {
    await session.replyToThread(commentModalState.threadId, draft);
    commentState.replyDraft = "";
    renderCommentPanel();
    renderCommentModal();
  }
}

async function archiveCommentModalThread(): Promise<void> {
  if (!session || commentModalState.mode !== "thread" || !commentModalState.threadId) {
    return;
  }

  await session.archiveThread(commentModalState.threadId);
  renderCommentPanel();
  renderCommentModal();
}

async function deleteCommentModalThread(): Promise<void> {
  if (!session || commentModalState.mode !== "thread" || !commentModalState.threadId) {
    return;
  }

  const threadId = commentModalState.threadId;
  await session.deleteThread(threadId);
  if (commentState.activeThreadId === threadId) {
    commentState.activeThreadId = null;
  }
  closeCommentModal();
  renderCommentPanel();
}

function updateCommentModalSubmitState(): void {
  elements.commentModalSubmit.disabled = elements.commentModalInput.value.trim().length === 0;
}

function createThreadTarget(selection: LocalSelectionState): CommentTarget {
  const maybeTableTarget = detectTableCellTarget(selection);
  if (maybeTableTarget) {
    return maybeTableTarget;
  }

  return {
    kind: "text-range",
    start: Math.min(selection.selection.anchor, selection.selection.head),
    end: Math.max(selection.selection.anchor, selection.selection.head),
    quote:
      selection.selectedText.trim().length > 0
        ? truncateSelection(selection.selectedText)
        : `${selection.selection.anchor + 1} → ${selection.selection.head + 1}`
  };
}

function isCommentableSelection(selection: LocalSelectionState | null): selection is LocalSelectionState {
  return Boolean(
    selection &&
      selection.selection.anchor !== selection.selection.head &&
      selection.selectedText.trim().length > 0
  );
}

function renderSelectionPopover(): void {
  const popover = elements.commentPopover;
  const selection = isCommentableSelection(currentSelection) ? currentSelection : null;
  const view = session?.getEditorView() ?? null;

  if (!session || !selection || !view || !view.hasFocus) {
    popover.classList.remove("is-visible");
    popover.setAttribute("aria-hidden", "true");
    popover.style.left = "";
    popover.style.top = "";
    popover.dataset.placement = "above";
    return;
  }

  const coords = view.coordsAtPos(Math.max(selection.selection.anchor, selection.selection.head));
  if (!coords) {
    popover.classList.remove("is-visible");
    popover.setAttribute("aria-hidden", "true");
    popover.style.left = "";
    popover.style.top = "";
    popover.dataset.placement = "above";
    return;
  }

  popover.classList.add("is-visible");
  popover.setAttribute("aria-hidden", "false");

  const padding = 12;
  const popoverWidth = popover.offsetWidth;
  const popoverHeight = popover.offsetHeight;
  const frameWidth = elements.editorFrame.clientWidth;
  const frameHeight = elements.editorFrame.clientHeight;
  const frameRect = elements.editorFrame.getBoundingClientRect();
  const anchorLeft = coords.left - frameRect.left;
  const anchorTop = coords.top - frameRect.top;
  const centeredLeft = anchorLeft - popoverWidth / 2;
  const left = Math.max(padding, Math.min(centeredLeft, frameWidth - popoverWidth - padding));

  const aboveTop = anchorTop - popoverHeight - 12;
  const belowTop = anchorTop + 18;
  const useBelow = aboveTop < padding && belowTop + popoverHeight <= frameHeight - padding;
  const top = useBelow
    ? Math.min(belowTop, frameHeight - popoverHeight - padding)
    : Math.max(padding, aboveTop);

  popover.dataset.placement = useBelow ? "below" : "above";
  popover.style.left = `${left}px`;
  popover.style.top = `${top}px`;
}

function describeThreadTarget(target: CommentTarget): string {
  switch (target.kind) {
    case "text-range":
      return `Text range · ${target.quote || `${target.start + 1} → ${target.end + 1}`}`;
    case "table-cell":
      return `Table cell · row ${target.row}, column ${target.column}: ${target.text}`;
    case "image-position":
      return `Image comment · ${target.imagePath} @ ${Math.round(target.x * 100)}%, ${Math.round(
        target.y * 100
      )}%`;
  }
}

function detectTableCellTarget(selection: LocalSelectionState): CommentTarget | null {
  const trimmed = selection.selectedText.trim();
  if (!trimmed.includes("|")) {
    return null;
  }

  const firstLine = trimmed.split("\n").find((line) => line.includes("|")) ?? trimmed;
  const cells = firstLine.split("|").map((cell) => cell.trim()).filter(Boolean);

  return {
    kind: "table-cell",
    start: Math.min(selection.selection.anchor, selection.selection.head),
    end: Math.max(selection.selection.anchor, selection.selection.head),
    row: 1,
    column: Math.max(1, cells.length),
    text: truncateSelection(trimmed)
  };
}

function formatRelativeTime(timestamp: number): string {
  const elapsed = Date.now() - timestamp;
  if (elapsed < 60_000) {
    return "just now";
  }

  const minutes = Math.round(elapsed / 60_000);
  if (minutes < 60) {
    return `${minutes}m ago`;
  }

  const hours = Math.round(minutes / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }

  const days = Math.round(hours / 24);
  return `${days}d ago`;
}

function truncateSelection(value: string): string {
  const normalized = value.replace(/\s+/g, " ").trim();
  if (normalized.length <= 80) {
    return normalized;
  }

  return `${normalized.slice(0, 77)}...`;
}

function summarizeSelection(selection: CollaboratorSelection, value: string): string {
  if (selection.anchor === selection.head) {
    return `Caret at ${selection.anchor + 1}`;
  }

  const from = Math.min(selection.anchor, selection.head);
  const to = Math.max(selection.anchor, selection.head);
  const selectedText = value.slice(from, to);
  return `Selected ${to - from} characters: ${truncateSelection(selectedText) || "untitled"}`;
}

function applyTheme(theme: ThemeMode): void {
  document.body.dataset.theme = theme;
}

function applyPrimaryColor(color: string): void {
  document.body.style.setProperty("--primary", color);
  document.body.style.setProperty("--primary-soft", `color-mix(in srgb, ${color} 18%, transparent)`);
  document.body.style.setProperty("--primary-strong", `color-mix(in srgb, ${color} 82%, black)`);
}

function applyEditorFontSize(size: number): void {
  document.body.style.setProperty("--editor-font-size", `${size}px`);
}

function applyCommentsPaneWidth(width: number): void {
  document.body.style.setProperty("--comments-pane-width", `${width}px`);
}

function setThemePreference(theme: ThemeMode): void {
  pendingTheme = theme;
  applyTheme(pendingTheme);
  renderSettingsMenu();
  const preferences = loadPreferences();
  savePreferences({
    ...preferences,
    theme: pendingTheme,
    primaryColor: pendingPrimaryColor
  });
}

function renderSettingsMenu(): void {
  elements.themeLight.classList.toggle("is-active", pendingTheme === "light");
  elements.themeDark.classList.toggle("is-active", pendingTheme === "dark");
  elements.editorFontSize.value = String(pendingEditorFontSize);
  elements.editorFontSizeValue.textContent = `${pendingEditorFontSize}px`;
  elements.primaryColor.value = pendingPrimaryColor;
  elements.primaryColorValue.textContent = pendingPrimaryColor.toUpperCase();
}

function updateEditorMetrics(): void {
  const content = session?.getDocumentText() ?? "";
  const words = content.trim().length === 0 ? 0 : content.trim().split(/\s+/).length;
  const characters = content.length;
  elements.editorSurface.classList.toggle("is-empty", content.length === 0);
  elements.wordCount.textContent = `${words} word${words === 1 ? "" : "s"}`;
  elements.characterCount.textContent = `${characters} character${characters === 1 ? "" : "s"}`;
  elements.selectionState.textContent = currentSelection ? currentSelection.summary : "No selection";
}

function loadThemePreference(): ThemeMode {
  const preferences = loadPreferences();
  if (preferences.theme === "dark" || preferences.theme === "light") {
    return preferences.theme;
  }

  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function loadPrimaryColorPreference(): string {
  const preferences = loadPreferences();
  return normalizePrimaryColor(preferences.primaryColor ?? "#f2c424");
}

function loadEditorFontSizePreference(): number {
  return normalizeEditorFontSize(loadPreferences().editorFontSize ?? 16);
}

function loadCommentsPaneWidthPreference(): number {
  return normalizeCommentsPaneWidth(loadPreferences().commentsPaneWidth ?? 360);
}

function normalizePrimaryColor(value: string): string {
  return /^#[0-9a-f]{6}$/i.test(value) ? value.toLowerCase() : "#f2c424";
}

function normalizeEditorFontSize(value: number): number {
  return Math.max(14, Math.min(24, Math.round(Number.isFinite(value) ? value : 16)));
}

function normalizeCommentsPaneWidth(value: number): number {
  return Math.max(280, Math.min(560, Math.round(Number.isFinite(value) ? value : 360)));
}

function loadPreferences(): WebPreferences {
  try {
    const value = window.localStorage.getItem(storageKey);
    return value ? (JSON.parse(value) as WebPreferences) : {};
  } catch {
    return {};
  }
}

function savePreferences(preferences: WebPreferences): void {
  window.localStorage.setItem(storageKey, JSON.stringify(preferences));
}

function setStatus(message: string): void {
  elements.syncStatus.textContent = message;
}

function generateFallbackName(seed: string): string {
  const adjective = ["Calm", "Bright", "Amber", "Gentle", "Swift", "Quiet"][hashSeed(seed) % 6];
  const noun = ["Otter", "Juniper", "Comet", "Lantern", "Maple", "Sparrow"][hashSeed(`${seed}-noun`) % 6];
  const suffix = String(hashSeed(`${seed}-suffix`) % 1000).padStart(3, "0");
  return `${adjective} ${noun} ${suffix}`;
}

function getInitials(value: string): string {
  const parts = value.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return "U";
  }

  return parts
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
}

function hashSeed(value: string): number {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 33 + value.charCodeAt(index)) >>> 0;
  }
  return hash;
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function getElement<T extends HTMLElement>(id: string): T {
  const element = document.getElementById(id);
  if (!element) {
    throw new Error(`Missing required element: ${id}`);
  }
  return element as T;
}
